import { Card, CardRadioButton, Column, Row, Text } from '@amzn/meridian';
import {
  AllMarketplacesPreview,
  AllPlatformsPreview,
  NoSharingPreview,
  PerMarketplacePreview,
  SameLanguagePreview,
  SeparatePlatformsPreview,
} from 'components/MarketplacePreview/PreviewComponents';
import type { FC } from 'react';
import { stylesheet } from 'typestyle';
import type { SharingCardOption } from './constants';

const css = stylesheet({
  disabledPreview: {
    opacity: 0.5,
  },
  tagline: {
    display: 'inline-block',
  },
});

// Local component mapping for preview components
const PREVIEW_COMPONENT_MAP = {
  'marketplace-no-sharing': NoSharingPreview,
  'marketplace-share-all': AllMarketplacesPreview,
  'marketplace-share-per-marketplace': PerMarketplacePreview,
  'marketplace-share-same-language': SameLanguagePreview,
  'platform-no-sharing': SeparatePlatformsPreview,
  'platform-share-all': AllPlatformsPreview,
} as const;

export const SharingOptionCard: FC<{
  option: SharingCardOption;
  isChecked: boolean;
  onSelect: (cardId: string) => void;
  isDefault: boolean;
  isDisabled: boolean;
}> = ({ option, isChecked, onSelect, isDefault, isDisabled }) => {
  const PreviewComponent = PREVIEW_COMPONENT_MAP[option.id as keyof typeof PREVIEW_COMPONENT_MAP];

  return (
    <Card
      aria-disabled={isDisabled}
      checked={isChecked}
      data-cy={`sharing-option-${option.id}`}
      disabled={isDisabled}
      maxWidth={300}
      minWidth={250}
      onClick={() => onSelect(option.id)}
      spacingInset="400"
    >
      <Column alignmentHorizontal="start" spacing="400 0">
        <Row alignmentVertical="top" height="65px">
          <Column alignmentHorizontal="start" width="100%">
            <Text color={isDisabled ? 'secondary' : 'primary'}>
              {option.label}{' '}
              <i className={css.tagline}>
                {isDefault && '(default)'}
                {isDisabled && !isDefault && '(not supported)'}
              </i>
            </Text>
          </Column>
          <Column alignmentHorizontal="end">
            <CardRadioButton aria-labelledby={`${option.id}-label`} data-cy={option.id} id={option.id} />
          </Column>
        </Row>
        <Row className={isDisabled ? css.disabledPreview : undefined} data-cy={`${option.id}-preview`} width="100%">
          {PreviewComponent ? <PreviewComponent /> : null}
        </Row>
      </Column>
    </Card>
  );
};
